import { useNavigate } from "react-router-dom";
import { LeanWordmark } from "../components/Logo";
import { Salad, Sparkles, Handshake, GraduationCap, ArrowRight, Lock, MessageCircle, Instagram } from "lucide-react";
import priyankaImg from "../assets/priyanka_speaking.jpg";

const HERO = "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const BOWL = "https://images.unsplash.com/photo-1547592180-85f173990554?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const GREENS = "https://images.unsplash.com/photo-1580910365148-baad301313ad?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const WHATSAPP = "https://wa.me/917795009798?text=Hi%20Lean%20by%20Priyanka%2C%20I%27d%20like%20to%20know%20more%20about%20your%20nutrition%20programs.";
const INSTAGRAM = "https://www.instagram.com/leanbypriyancka";

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Nav */}
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur border-b border-border/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-3">
          <LeanWordmark className="h-8 sm:h-9 w-auto" />
          <nav className="ml-auto flex items-center gap-1 sm:gap-2">
            <a href="#services" className="hidden sm:inline-block rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Services</a>
            <a href={WHATSAPP} target="_blank" rel="noreferrer" className="hidden sm:inline-block rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Contact</a>
            <a href={INSTAGRAM} target="_blank" rel="noreferrer" title="Instagram @leanbypriyancka" data-testid="nav-instagram-link" className="inline-grid place-items-center h-9 w-9 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-emerald-600">
              <Instagram className="h-5 w-5" />
            </a>
            <button
              onClick={() => navigate("/login")}
              data-testid="finance-portal-button"
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-neutral-800 transition-colors"
            >
              <Lock className="h-3.5 w-3.5" /> Employee Login
            </button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-16 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        <div className="animate-fade-up">
          <span className="inline-flex items-center gap-2 rounded-full bg-accent px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-accent-foreground">
            <Sparkles className="h-3.5 w-3.5" /> Our website is launching soon
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Nourishment,<br /><span className="text-emerald-600">personalised</span> for you.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-md leading-relaxed">
            Science-backed nutrition coaching by Priyanka — sustainable plans that fit real life. The full experience is on its way. Say hello in the meantime.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={WHATSAPP} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-3.5 font-semibold text-white hover:bg-emerald-700 transition-colors shadow-sm">
              <MessageCircle className="h-5 w-5" /> Enquire on WhatsApp
            </a>
            <a href="#services"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3.5 font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
              What's coming <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <a href={INSTAGRAM} target="_blank" rel="noreferrer" data-testid="hero-instagram-link"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-600 transition-colors">
            <Instagram className="h-4 w-4" /> Follow @leanbypriyancka on Instagram
          </a>
        </div>

        <div className="relative animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <div className="absolute -inset-4 -z-10 rounded-[2.5rem] bg-accent/70 blur-2xl" />
          <img src={HERO} alt="Fresh, balanced nutrition bowl" className="w-full aspect-[4/5] sm:aspect-square object-cover rounded-[2rem] shadow-xl border border-white" />
          <div className="absolute -bottom-5 -left-3 sm:-left-6 bg-white rounded-2xl shadow-lg border border-slate-100 px-4 py-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-emerald-100 grid place-items-center text-emerald-600"><Sparkles className="h-5 w-5" /></div>
            <div>
              <p className="text-sm font-bold leading-none">Real, lasting results</p>
              <p className="text-xs text-muted-foreground mt-0.5">1-on-1 nutrition coaching</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-neutral-950 text-white py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-400">What's coming</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Everything to help you thrive</h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            <ServiceCard icon={Salad} title="Personalised Nutrition" text="Custom plans built around your body, goals, routine and food you actually enjoy." img={BOWL} />
            <ServiceCard icon={Handshake} title="Brand Collaborations" text="Trusted partnerships with wellness and food brands that share our values." img={GREENS} />
            <ServiceCard icon={GraduationCap} title="Workshops & Talks" text="Practical, myth-busting sessions on everyday nutrition and healthy habits — led by Priyanka." img={priyankaImg} imgClass="aspect-[7/10] object-cover object-center" />
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 text-center">
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Want to start your journey?</h2>
        <p className="mt-3 text-muted-foreground">We're onboarding a limited number of clients. Reach out and we'll get you set up.</p>
        <a href={WHATSAPP} target="_blank" rel="noreferrer"
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-7 py-3.5 font-semibold text-white hover:bg-emerald-700 transition-colors shadow-sm">
          <MessageCircle className="h-5 w-5" /> Message us on WhatsApp
        </a>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LeanWordmark className="h-9 w-auto" />
            <p className="text-xs text-muted-foreground border-l border-border pl-3">© {new Date().getFullYear()} · Nutrition &amp; Wellness</p>
          </div>
          <div className="flex items-center gap-5 text-sm text-muted-foreground">
            <a href={INSTAGRAM} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-foreground" data-testid="footer-instagram-link">
              <Instagram className="h-4 w-4" /> Instagram
            </a>
            <a href={WHATSAPP} target="_blank" rel="noreferrer" className="hover:text-foreground">WhatsApp</a>
            <button onClick={() => navigate("/login")} className="inline-flex items-center gap-1.5 hover:text-foreground" data-testid="footer-finance-link">
              <Lock className="h-3.5 w-3.5" /> Employee Login
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ServiceCard({ icon: Icon, title, text, img, imgClass = "aspect-[7/10] object-cover object-center" }) {
  return (
    <div className="rounded-2xl bg-neutral-900 border border-neutral-800 overflow-hidden hover:border-emerald-500/50 transition-colors">
      {img && <img src={img} alt={title} className={`w-full ${imgClass}`} />}
      <div className="p-6">
        <div className="h-10 w-10 rounded-xl bg-emerald-500/15 grid place-items-center text-emerald-400">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="mt-4 text-lg font-bold">{title}</h3>
        <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}
